import { connect } from 'react-redux';
import i18next from 'i18next';
import moment from 'moment-timezone';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { openGenericModal } from '@actions/modal';
import { showErrorMessage } from '@actions/messageconfirmation';

import { Button, ToggleSwitch } from '@commons/utils/styledLibraryComponents';
import { DATE_DISPLAY_FORMATS, TIME_DISPLAY_FORMATS } from '@commons/DatePickers/constants';
import { DEFAULT_TIMEZONE } from '@commons/utils/timezone';
import {
  getChannelSelectionItems,
  getClientStoreNameTranslation,
} from '@commons/utils/translations';
import EmptyState from '@commons/EmptyState';
import Text, { ENUM_COLORS, ENUM_FONTS } from '@commons/Text';

import { getClientInfo } from '@selectors/client';

import { externalClient as externalClientService } from '@services/externalClient';

import { buildCatalogCategoryGroups } from '../../utils/buildCatalogCategoryGroups';
import { buildQuantitiesByProductId } from '../../utils/buildOrderQuantities';
import { exportExternalOrder } from '../../utils/export';
import { EXTERNAL_ORDER_STATUS } from '../../constants';
import {
  getDeleteOrderModal,
  getLeaveOrderFormModal,
  getValidateDraftModal,
} from '../../utils/getExternalOrderModals';
import { getExternalOrderFormActions } from '../../utils/getExternalOrderFormActions';
import {
  isChannelFieldDisplayed,
  parseDeliveryDate,
  parseDeliveryTime,
} from '../../utils/externalOrderHeaderFields';
import { useCollapsedCategories } from '../../hooks/useCollapsedCategories';
import { useExternalClientsOfStores } from '../../hooks/useExternalClientsOfStores';
import OrderClientPanel from '../OrderClientPanel';
import OrderHeaderSelectors from '../OrderHeaderSelectors';
import OrderTableHeader from '../OrderTableHeader';
import OrderTopBar from '../OrderTopBar';

import { Content, FormContainer, SelectorsBar, TableCard } from '../orderFormStyledComponents';

import OrderCatalogTable from './components/OrderCatalogTable';

import { ToggleContainer } from './styledComponents';

const SUMMARY_TOGGLE_ID = 'external-order-summary-toggle';

/**
 * Reference previewed while the order is not saved yet. It mirrors what the creation route builds
 * server-side (`EXT-{store partner id}-{client external reference}-{timestamp}`), including the
 * deliberately empty segments when the store has no partner id or the client no external reference.
 */
const buildReferencePreview = (store, externalClient) => {
  const timestamp = moment
    .tz(store?.timezone || DEFAULT_TIMEZONE)
    .format(DATE_DISPLAY_FORMATS.COMPACT_DAY_MONTH_YEAR_HOUR_MINUTE_SECOND);

  return `EXT-${store?.partnerId || ''}-${externalClient?.externalReference || ''}-${timestamp}`;
};

/**
 * Form of a draft external order, shared by the creation and the consultation pages. The layout is a
 * form and not a log: the whole active catalog of the client is displayed, grouped by category, and
 * a line is ordered by typing its quantity. A duplication (COR-1691) is a creation opening on a
 * restored `externalOrder`: the prefilled payload of its source, which exists nowhere server-side.
 *
 * The header fields drive everything else — the table, the client panel and the comment stay hidden
 * behind an empty state until the store, the client, the delivery date / time and (on a
 * multi-channel platform serving a sales point) the channel are set. The catalog is reloaded on
 * every store type / client change, since a central kitchen only prepares central products.
 *
 * Saving is possible even with no ordered line, while validating the draft requires at least one
 * quantity greater than 0.
 */
export const ExternalOrderForm = (props) => {
  const {
    externalOrder,
    isCreation,
    canCreateAndEditDraft,
    canValidateDraft,
    canDeleteDraft,
    isSaving,
    onSave,
    onValidateDraft,
    onDelete,
    onClose,
    // From Redux
    stores,
    storeName,
    channels,
    hasMultipleChannels,
    defaultChannelId,
    currency,
    openModal,
    displayErrorMessage,
  } = props;

  const [selectedStore, setSelectedStore] = useState(null);
  const [selectedExternalClient, setSelectedExternalClient] = useState(null);
  const [deliveryDate, setDeliveryDate] = useState(null);
  const [deliveryTime, setDeliveryTime] = useState(null);
  const [selectedChannel, setSelectedChannel] = useState(null);
  const [comment, setComment] = useState('');

  const [externalClientDetail, setExternalClientDetail] = useState(null);
  const [catalog, setCatalog] = useState([]);
  const [quantityByProductId, setQuantityByProductId] = useState({});

  const [search, setSearch] = useState('');
  const [isSummaryMode, setIsSummaryMode] = useState(false);

  const { collapsedCategories, toggleCategory, resetCollapsedCategories } =
    useCollapsedCategories();

  // Only what the user typed counts as a change: the values restored from a saved order do not.
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  /**
   * A saved order is restored into the fields exactly once — header, client and product lines apart,
   * since they do not resolve at the same time. Replaying any of them would silently undo what the
   * user changed since: the client reset that follows a store change, or a table typed from scratch
   * after one.
   */
  const hasRestoredHeaderRef = useRef(false);
  const hasRestoredExternalClientRef = useRef(false);
  const hasRestoredProductsRef = useRef(false);

  // The default channel of a new order is imposed once, and left to the user from then on.
  const hasPrefilledChannelRef = useRef(false);

  const status = externalOrder?.status ?? EXTERNAL_ORDER_STATUS.DRAFT;

  const translatedStoreName = getClientStoreNameTranslation(storeName);

  const isReadOnly = !canCreateAndEditDraft;

  const isChannelDisplayed = isChannelFieldDisplayed({
    hasMultipleChannels,
    store: selectedStore,
  });

  /**
   * The reference of a saved order, or its preview once the store and the client — its only two
   * variable parts — are known. Before that there is nothing to show, not even a skeleton of it.
   *
   * A saved reference only stands while the selection is still the pair it was built on: the save
   * rebuilds it whenever the store or the client changes, so the preview takes over as soon as the
   * selection leaves the saved pair — and the saved reference comes back if the user reverts to it.
   */
  const reference = useMemo(() => {
    if (!selectedStore || !selectedExternalClient) {
      return externalOrder?.reference ?? '';
    }

    const isSavedHeaderSelected =
      selectedStore.id === externalOrder?.storeId &&
      selectedExternalClient.id === externalOrder?.externalClientId;

    if (externalOrder?.reference && isSavedHeaderSelected) {
      return externalOrder.reference;
    }

    return buildReferencePreview(selectedStore, selectedExternalClient);
  }, [externalOrder, selectedStore, selectedExternalClient]);

  /**
   * The delivery time is deliberately left out: an order is delivered on a day, at an hour only when
   * the client asked for one. The catalog loads, the draft saves and the draft validates without it.
   */
  const isHeaderComplete =
    !!selectedStore &&
    !!selectedExternalClient &&
    !!deliveryDate &&
    (!isChannelDisplayed || !!selectedChannel);

  const orderedProductIds = useMemo(
    () =>
      Object.keys(quantityByProductId).filter((productId) => quantityByProductId[productId] > 0),
    [quantityByProductId],
  );

  const totalWithTaxes = useMemo(
    () =>
      catalog.reduce((total, { productId, priceWithTaxes }) => {
        const quantity = quantityByProductId[productId] ?? 0;

        return total + quantity * priceWithTaxes;
      }, 0),
    [catalog, quantityByProductId],
  );

  const categoryGroups = useMemo(
    () => buildCatalogCategoryGroups(catalog, { search, quantityByProductId, isSummaryMode }),
    [catalog, search, quantityByProductId, isSummaryMode],
  );

  /**
   * Stores selectable in the header, narrowed by two composing filters. A selected client leaves
   * only the stores it is associated with — read from its detail, so the filter only applies once
   * it has resolved, the whole list standing in for the brief in-flight window. And as soon as a
   * line is ordered, only the stores preparing that same product type are left: a central kitchen
   * prepares central products, a sales point classic ones, and an order never mixes both.
   */
  const selectableStores = useMemo(() => {
    const associatedStoreIds = externalClientDetail
      ? new Set((externalClientDetail.stores ?? []).map(({ id }) => id))
      : null;

    const isSameTypeRequired = !!orderedProductIds.length && !!selectedStore;

    return stores.filter((store) => {
      if (associatedStoreIds && !associatedStoreIds.has(store.id)) {
        return false;
      }

      if (isSameTypeRequired && store.isKitchen !== selectedStore.isKitchen) {
        return false;
      }

      return true;
    });
  }, [stores, orderedProductIds, selectedStore, externalClientDetail]);

  const selectableChannels = useMemo(() => getChannelSelectionItems(channels), [channels]);

  const selectedStoreIds = useMemo(
    () => (selectedStore ? [selectedStore.id] : []),
    [selectedStore],
  );

  const handleExternalClientsError = useCallback(() => {
    displayErrorMessage(i18next.t('EXTERNAL_ORDERS.FORM.CLIENTS_FETCH_ERROR'));
  }, [displayErrorMessage]);

  const externalClients = useExternalClientsOfStores({
    storeIds: selectedStoreIds,
    onError: handleExternalClientsError,
  });

  /**
   * Earliest selectable delivery date: the current day of the store. An order restored with a past
   * delivery date — saved or duplicated — keeps showing it: the picker only restricts what can be
   * *chosen*, no other past day can be selected. An unknown store timezone falls back on the
   * platform one: `moment.tz` would otherwise build an invalid date, and every comparison against it
   * being false, the whole calendar would become selectable.
   */
  const earliestDeliveryDate = useMemo(() => {
    const currentDayInStoreTimezone = moment.tz(selectedStore?.timezone || DEFAULT_TIMEZONE);

    if (!currentDayInStoreTimezone.isValid()) {
      return moment.tz(DEFAULT_TIMEZONE).startOf('day');
    }

    return currentDayInStoreTimezone.startOf('day');
  }, [selectedStore]);

  /**
   * Restore the saved order into the form fields. It carries ids, the dropdowns hold objects, so the
   * restore waits for the lists to resolve them against — the store list and the channels come from
   * the store, the client list from the store selection below.
   *
   * It runs exactly once: the client list is refetched on every store change, and replaying the
   * restore then would re-impose the client of the order right after the change emptied it.
   */
  useEffect(() => {
    if (hasRestoredHeaderRef.current || !externalOrder || !stores.length) {
      return;
    }

    const restoredStore = stores.find(({ id }) => id === externalOrder.storeId) ?? null;

    setSelectedStore(restoredStore);
    setDeliveryDate(parseDeliveryDate(externalOrder.deliveryDate));
    setDeliveryTime(parseDeliveryTime(externalOrder.deliveryTime));
    setSelectedChannel(selectableChannels.find(({ id }) => id === externalOrder.channelId) ?? null);
    setComment(externalOrder.comment ?? '');

    // The client is resolved apart: its list is only fetched once the store above is selected.
    hasRestoredHeaderRef.current = true;
  }, [externalOrder, stores, selectableChannels]);

  /**
   * A new order opens on the default channel of the platform, the one most of them are delivered on.
   * It is a default and not a change: the save button stays asleep until the user touches something.
   *
   * It runs once, and never on a restored order — saved or duplicated, the restore above owns its
   * channel and would be silently overridden here.
   */
  useEffect(() => {
    if (
      !isCreation ||
      externalOrder ||
      hasPrefilledChannelRef.current ||
      !selectableChannels.length
    ) {
      return;
    }

    hasPrefilledChannelRef.current = true;

    setSelectedChannel(selectableChannels.find(({ id }) => id === defaultChannelId) ?? null);
  }, [isCreation, externalOrder, selectableChannels, defaultChannelId]);

  /**
   * Select the client of the restored order among the ones of its store, so the form owns the same
   * object as the dropdown does. Once done the client belongs to the user: the list is refetched on
   * every store change, and re-selecting here would undo the reset that change performs.
   */
  useEffect(() => {
    if (
      hasRestoredExternalClientRef.current ||
      !externalOrder?.externalClientId ||
      !externalClients.length
    ) {
      return;
    }

    hasRestoredExternalClientRef.current = true;

    setSelectedExternalClient(
      externalClients.find(({ id }) => id === externalOrder.externalClientId) ?? null,
    );
  }, [externalOrder, externalClients]);

  /**
   * Catalog and client information behind the table and the side panel. Both follow the client and
   * the type of the selected store, so they are reloaded together — everything typed in the table is
   * dropped as soon as one of them changes, since it was entered against another catalog, along with
   * the search and the collapsed categories which described that other catalog too.
   *
   * The lines of a saved order are restored on the first load, and on it only: changing the store or
   * the client drops them for good rather than carrying them over to a catalog they were never
   * ordered from, and coming back does not resurrect them.
   */
  useEffect(() => {
    let isCurrentQuery = true;

    const resetCatalogState = () => {
      setCatalog([]);
      setExternalClientDetail(null);
      setQuantityByProductId({});
      setSearch('');
      resetCollapsedCategories();
    };

    const fetchCatalogAndClient = async () => {
      if (!selectedExternalClient || !selectedStore) {
        resetCatalogState();

        return;
      }

      // Cleared right away rather than once the new catalog lands: until then the table would keep
      // showing the previous client's rows, editable, and the total would be computed on them.
      resetCatalogState();

      try {
        const [{ catalog: catalogLines }, clientDetail] = await Promise.all([
          externalClientService.getFullExternalClientCatalog(selectedExternalClient.id, {
            isKitchen: !!selectedStore.isKitchen,
          }),
          externalClientService.getExternalClient(selectedExternalClient.id),
        ]);

        if (!isCurrentQuery) {
          return;
        }

        const catalogRows = catalogLines ?? [];

        const shouldRestoreSavedLines =
          !hasRestoredProductsRef.current &&
          externalOrder?.storeId === selectedStore.id &&
          externalOrder?.externalClientId === selectedExternalClient.id;

        // Set whatever happens: a catalog loaded once is a table the user owns from now on
        hasRestoredProductsRef.current = true;

        setCatalog(catalogRows);
        setExternalClientDetail(clientDetail);
        setQuantityByProductId(
          shouldRestoreSavedLines
            ? buildQuantitiesByProductId(externalOrder.products, catalogRows)
            : {},
        );
      } catch {
        if (!isCurrentQuery) {
          return;
        }

        // Quantities included: keeping them would leave the form able to save product ids that do
        // not belong to the current client, while the total is computed on an empty catalog.
        resetCatalogState();

        displayErrorMessage(i18next.t('EXTERNAL_ORDERS.FORM.CATALOG_FETCH_ERROR'));
      }
    };

    fetchCatalogAndClient();

    return () => {
      isCurrentQuery = false;
    };
  }, [selectedExternalClient, selectedStore?.isKitchen]);

  /**
   * The summary mode only makes sense while at least one line is ordered: emptying the order from
   * that very view switches it back off, as its toggle becomes disabled.
   */
  useEffect(() => {
    if (orderedProductIds.length) {
      return;
    }

    setIsSummaryMode(false);
  }, [orderedProductIds]);

  const buildOrderPayload = (targetStatus) => ({
    status: targetStatus,
    storeId: selectedStore.id,
    externalClientId: selectedExternalClient.id,
    deliveryDate: deliveryDate.format(DATE_DISPLAY_FORMATS.DASHED_YEAR_MONTH_DAY),
    // Nullable: the order is delivered at an hour only when the client asked for one.
    deliveryTime: deliveryTime
      ? deliveryTime.format(TIME_DISPLAY_FORMATS.SLASHED_HOUR_MINUTE)
      : null,
    /**
     * The channel field is hidden for a central kitchen, which does not distinguish channels, and on
     * a mono-channel platform. The creation route still requires one as soon as the platform has
     * several, so the platform default stands in — sending null would answer a 422.
     */
    channelId: isChannelDisplayed ? selectedChannel.id : defaultChannelId ?? null,
    comment: comment || null,
    products: orderedProductIds.map((productId) => ({
      productId,
      quantity: quantityByProductId[productId],
    })),
  });

  /**
   * Changing the store only resets the selected client when it does not survive the change: a client
   * also mapped to the new store, of the same type as the previous one (central kitchen or sales
   * point), stays orderable with the very same catalog — its lines and prices belong to the client,
   * not to the store — so the quantities already typed are kept. In any other case the client is
   * reset as before, and the catalog with it. The mapped stores are read from the client detail,
   * which resolves with the catalog: while it is still in flight the client is conservatively reset.
   */
  const handleSelectStore = (store) => {
    setHasUnsavedChanges(true);
    setSelectedStore(store);

    const isSameStoreType = store?.isKitchen === selectedStore?.isKitchen;

    const isClientMappedToNewStore = !!externalClientDetail?.stores?.some(
      ({ id }) => id === store?.id,
    );

    if (isSameStoreType && isClientMappedToNewStore) {
      return;
    }

    // The client list depends on the store, so the previous choice cannot be assumed to survive it.
    setSelectedExternalClient(null);
  };

  const handleSelectExternalClient = (externalClient) => {
    setHasUnsavedChanges(true);
    setSelectedExternalClient(externalClient);
  };

  /**
   * An emptied or half-typed field hands over an invalid moment (the time picker) or null (the date
   * picker), and must leave the field empty rather than keep the previous value: the header would
   * otherwise stay complete and the order saveable on a value nobody can see anymore.
   */
  const handleChangeDeliveryDate = (newDeliveryDate) => {
    setHasUnsavedChanges(true);
    setDeliveryDate(newDeliveryDate?.isValid() ? newDeliveryDate : null);
  };

  const handleChangeDeliveryTime = (newDeliveryTime) => {
    setHasUnsavedChanges(true);
    setDeliveryTime(newDeliveryTime?.isValid() ? newDeliveryTime : null);
  };

  const handleSelectChannel = (channel) => {
    setHasUnsavedChanges(true);
    setSelectedChannel(channel);
  };

  const handleChangeComment = (newComment) => {
    setHasUnsavedChanges(true);
    setComment(newComment);
  };

  const handleChangeQuantity = useCallback((productId, value) => {
    setHasUnsavedChanges(true);
    setQuantityByProductId((currentQuantities) => ({
      ...currentQuantities,
      [productId]: Number(value) || 0,
    }));
  }, []);

  const handleClose = () => {
    if (isReadOnly || !hasUnsavedChanges) {
      onClose();

      return;
    }

    openModal(getLeaveOrderFormModal({ onConfirm: onClose }));
  };

  // Both entry points are already disabled on an incomplete header: the guard covers a click racing
  // a field being emptied, since the payload reads the header fields without checking them.
  const handleSave = () => {
    if (!isHeaderComplete) {
      return;
    }

    onSave(buildOrderPayload(EXTERNAL_ORDER_STATUS.DRAFT));
  };

  const handleValidateDraft = () => {
    if (!isHeaderComplete) {
      return;
    }

    openModal(
      getValidateDraftModal({
        onConfirm: () => {
          onValidateDraft(buildOrderPayload(EXTERNAL_ORDER_STATUS.TO_COME));
        },
      }),
    );
  };

  const handleDelete = useCallback(() => {
    openModal(getDeleteOrderModal({ onConfirm: onDelete }));
  }, [openModal, onDelete]);

  /**
   * Export the order as the summary mode displays it (COR-1687): one row per ordered line, grouped
   * by category as the table is, whatever the search — the export always carries the whole order.
   * On an order not saved yet, the reference is the preview and the creation date stays empty.
   *
   * The VAT rate of a line is the one of its product on the channel of the order — resolved as the
   * save payload does, the platform default standing in when the channel field is hidden — falling
   * back on the base rate of the product, exactly as the validation snapshots it server-side.
   */
  const handleExport = useCallback(() => {
    const orderedCategoryGroups = buildCatalogCategoryGroups(catalog, {
      search: '',
      quantityByProductId,
      isSummaryMode: true,
    });

    const orderChannelId = isChannelDisplayed ? selectedChannel?.id : defaultChannelId;

    const orderedProductLines = orderedCategoryGroups.flatMap(({ products }) =>
      products.map((product) => ({
        ...product,
        quantity: quantityByProductId[product.productId],
        vatRate: product.vatRateByChannelId?.[orderChannelId] ?? product.vatRate,
      })),
    );

    exportExternalOrder({
      reference,
      clientStoreName: storeName,
      storeName: selectedStore?.name,
      externalClientName: selectedExternalClient?.name,
      externalClientReference: selectedExternalClient?.externalReference,
      deliveryDate,
      createdAt: externalOrder?.createdAt,
      productLines: orderedProductLines,
      currency,
    });
  }, [
    catalog,
    quantityByProductId,
    reference,
    storeName,
    selectedStore,
    selectedExternalClient,
    deliveryDate,
    externalOrder,
    currency,
    isChannelDisplayed,
    selectedChannel,
    defaultChannelId,
  ]);

  const actions = useMemo(
    () =>
      getExternalOrderFormActions({
        canDeleteDraft,
        isCreation,
        onExport: handleExport,
        onDelete: handleDelete,
      }),
    [canDeleteDraft, isCreation, handleExport, handleDelete],
  );

  const renderCatalogHeader = () => (
    <OrderTableHeader
      lineCount={orderedProductIds.length}
      search={search}
      totalWithTaxes={totalWithTaxes}
      onSearchChange={setSearch}
    >
      <ToggleContainer>
        <ToggleSwitch
          checked={isSummaryMode}
          disabled={!orderedProductIds.length}
          handleClick={() => setIsSummaryMode(!isSummaryMode)}
          id={SUMMARY_TOGGLE_ID}
        />
        <Text color={ENUM_COLORS.DARKEST} font={ENUM_FONTS.TEXT_SMALL_HEIGHT_16}>
          {i18next.t('EXTERNAL_ORDERS.FORM.DISPLAY_SUMMARY')}
        </Text>
      </ToggleContainer>
    </OrderTableHeader>
  );

  const renderContent = () => {
    if (!isHeaderComplete) {
      return (
        <TableCard>
          <EmptyState
            icon={'/images/inpulse/no-data.svg'}
            label={i18next.t('EXTERNAL_ORDERS.FORM.HEADER_EMPTY_STATE')}
          />
        </TableCard>
      );
    }

    return (
      <>
        <TableCard>
          {renderCatalogHeader()}
          <OrderCatalogTable
            categoryGroups={categoryGroups}
            collapsedCategories={collapsedCategories}
            isReadOnly={isReadOnly}
            quantityByProductId={quantityByProductId}
            onChangeQuantity={handleChangeQuantity}
            onToggleCategory={toggleCategory}
          />
        </TableCard>
        {!!externalClientDetail && (
          <OrderClientPanel
            comment={comment}
            externalClient={externalClientDetail}
            isCommentReadOnly={isReadOnly}
            onChangeComment={handleChangeComment}
          />
        )}
      </>
    );
  };

  return (
    <FormContainer>
      <OrderTopBar
        // A duplicated order travels as `externalOrder` too, but exists nowhere server-side yet.
        externalOrderId={isCreation ? null : externalOrder?.id}
        isSaved={!isCreation}
        reference={reference}
        status={status}
        updatedAt={externalOrder?.updatedAt}
      >
        <Button
          color={'inpulse-grey'}
          handleClick={handleClose}
          icon={'/images/inpulse/close-white-small.svg'}
        />
        {!isReadOnly && (
          <Button
            color={'inpulse-outline'}
            handleClick={handleSave}
            icon={'/images/inpulse/save-black-small.svg'}
            isDisabled={!isHeaderComplete || isSaving}
          />
        )}
        {canValidateDraft && (
          <Button
            color={'inpulse-default'}
            handleClick={handleValidateDraft}
            icon={'/images/inpulse/check-white-small.svg'}
            isDisabled={!isHeaderComplete || !orderedProductIds.length || isSaving}
            label={i18next.t('EXTERNAL_ORDERS.FORM.VALIDATE_DRAFT')}
          />
        )}
      </OrderTopBar>

      <SelectorsBar>
        <OrderHeaderSelectors
          channels={selectableChannels}
          deliveryDate={deliveryDate}
          deliveryTime={deliveryTime}
          earliestDeliveryDate={earliestDeliveryDate}
          externalClients={externalClients}
          isChannelDisplayed={isChannelDisplayed}
          isReadOnly={isReadOnly}
          selectedChannel={selectedChannel}
          selectedExternalClient={selectedExternalClient}
          selectedStore={selectedStore}
          storeName={translatedStoreName}
          stores={selectableStores}
          onChangeDeliveryDate={handleChangeDeliveryDate}
          onChangeDeliveryTime={handleChangeDeliveryTime}
          onSelectChannel={handleSelectChannel}
          onSelectExternalClient={handleSelectExternalClient}
          onSelectStore={handleSelectStore}
        />
        {isHeaderComplete && (
          <Button
            actions={actions}
            color={'inpulse-outline'}
            iconDropdown={'/images/inpulse/ellipsis-black-small.svg'}
          />
        )}
      </SelectorsBar>

      <Content>{renderContent()}</Content>
    </FormContainer>
  );
};

ExternalOrderForm.propTypes = {
  externalOrder: PropTypes.shape({
    id: PropTypes.string,
    reference: PropTypes.string,
    status: PropTypes.string,
    storeId: PropTypes.string,
    externalClientId: PropTypes.string,
    channelId: PropTypes.string,
    deliveryDate: PropTypes.string,
    deliveryTime: PropTypes.string,
    comment: PropTypes.string,
    updatedAt: PropTypes.string,
  }),
  isCreation: PropTypes.bool.isRequired,
  canCreateAndEditDraft: PropTypes.bool.isRequired,
  canValidateDraft: PropTypes.bool.isRequired,
  canDeleteDraft: PropTypes.bool.isRequired,
  isSaving: PropTypes.bool.isRequired,
  onSave: PropTypes.func.isRequired,
  onValidateDraft: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

ExternalOrderForm.defaultProps = {
  externalOrder: null,
};

const mapStateToProps = (state) => {
  const { storeName, clientChannels, hasMultipleChannels, defaultChannelId } = getClientInfo(
    state.baseReducer.user,
  );

  return {
    stores: state.baseReducer.activeStores,
    storeName,
    channels: clientChannels,
    hasMultipleChannels,
    defaultChannelId,
    currency: state.baseReducer.currency,
  };
};

const mapDispatchToProps = (dispatch) => ({
  openModal: (params) => {
    dispatch(openGenericModal(params));
  },
  displayErrorMessage: (message) => {
    dispatch(showErrorMessage(message));
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(ExternalOrderForm);
